import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import RentalAttributes from './RentalAttributes';
import * as consts from '../lib/consts';
import { HeartIcon } from '@heroicons/react/24/solid';
import { ImageOrVid } from './ui/ImageOrVid';
import { SharePopover } from './SharePopover';
import useStore from '../lib/store';
import { useRental } from '../hooks/useRental';
import { ImageLightbox } from './ImageLightbox';
import { PhotoGalleryOverlay } from './PhotoGalleryOverlay';
import { FacebookPhotoNotice } from './FacebookPhotoNotice';

type RentalLocationState = {
	backgroundLocation?: unknown;
	displayCurrency?: string;
	keywordTerms?: string[];
	photoGallery?: boolean;
	lightboxIndex?: number;
};

const RentalDetail = ({ isModal = false, onDismiss }: { isModal?: boolean; onDismiss?: () => void } = {}) => {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const locationState = location.state as RentalLocationState | null;
	const currentLocationPath = `${location.pathname}${location.search}`;
	const currencyParam = new URLSearchParams(location.search).get('currency');
	const queryDisplayCurrency = consts.DISPLAY_CURRENCIES.some(currency => currency.value === currencyParam)
		? currencyParam
		: null;

	const displayCurrency = queryDisplayCurrency || locationState?.displayCurrency || consts.BASE_CURRENCY;
	const keywordTerms = locationState?.keywordTerms || [];

	const { rental, loading } = useRental(id);
	const galleryOpen = Boolean(locationState?.photoGallery);
	const lightboxIndex = typeof locationState?.lightboxIndex === 'number' ? locationState.lightboxIndex : null;
	const mediaGridRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (galleryOpen || lightboxIndex !== null) {
			mediaGridRef.current?.querySelectorAll<HTMLVideoElement>('video').forEach(v => v.pause());
		}
	}, [galleryOpen, lightboxIndex]);

	const likedRentalIds = useStore((state) => state.likedRentalIds);
	const toggleLike = useStore((state) => state.toggleLike);
	const setDisplayCurrency = useStore((state) => state.setDisplayCurrency);
	const renderStatus = (children: ReactNode) => {
		if (!isModal) return children;

		return (
			<div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-start overflow-y-auto overscroll-contain">
				<div className="bg-white max-w-lg w-full p-8 mt-16 rounded-2xl shadow-xl relative">
					<button
						onClick={onDismiss ?? (() => navigate(-1))}
						className="absolute top-3 right-4 text-gray-500 hover:text-gray-700 text-2xl"
					>
						×
					</button>
					{children}
				</div>
			</div>
		);
	};

	useEffect(() => {
		if (queryDisplayCurrency) setDisplayCurrency(queryDisplayCurrency);
	}, [queryDisplayCurrency, setDisplayCurrency]);

	useEffect(() => {
		if (isModal || galleryOpen || lightboxIndex !== null) {
			document.body.classList.add('overflow-hidden');
			return () => document.body.classList.remove('overflow-hidden');
		}
	}, [galleryOpen, isModal, lightboxIndex]);

	const openPhotoGallery = useCallback(() => {
		navigate(currentLocationPath, {
			state: { ...locationState, photoGallery: true },
			preventScrollReset: true,
		});
	}, [currentLocationPath, locationState, navigate]);

	const openLightbox = useCallback((index: number) => {
		navigate(currentLocationPath, {
			state: { ...locationState, photoGallery: true, lightboxIndex: index },
			preventScrollReset: true,
		});
	}, [currentLocationPath, locationState, navigate]);

	const closePhotoGallery = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	const closeLightbox = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	// Handle Escape when deeper photo overlays are not handling it themselves.
	useEffect(() => {
		if (lightboxIndex !== null) return;
		const handleKey = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return;
			if (galleryOpen) closePhotoGallery();
			else if (isModal) (onDismiss ?? (() => navigate(-1)))();
		};
		window.addEventListener('keydown', handleKey);
		return () => window.removeEventListener('keydown', handleKey);
	}, [closePhotoGallery, galleryOpen, lightboxIndex, isModal, navigate, onDismiss]);

	if (loading) return renderStatus(<p className="p-4 text-gray-600">Loading...</p>);
	if (!rental) return renderStatus(<p className="p-4 text-red-500">Rental not found.</p>);

	const isLiked = likedRentalIds.some((id) => id === rental.id);
	const mediaUrls = rental.media_urls;
	const facebookPostUrl = rental.source === 'facebook' ? rental.source_url : undefined;

	return (
		<div className={`${isModal ? 'fixed inset-0 bg-black/50 z-50 flex justify-center items-start overflow-y-auto overscroll-contain' : ''}`}>
			<div className={`bg-white ${mediaUrls.length > 1 ? 'max-w-6xl' : 'max-w-3xl'} w-full p-8 mt-16 rounded-2xl shadow-xl relative`}>
				{isModal && (
					<button
						onClick={onDismiss ?? (() => navigate(-1))}
						className="absolute top-3 right-4 text-gray-500 hover:text-gray-700 text-2xl"
					>
						×
					</button>
				)}

				<div className="mx-auto">
					{isModal ? (
						<button onClick={onDismiss ?? (() => navigate(-1))} className="mb-6 text-sm text-blue-600 hover:text-blue-800 underline">
							← Back to listings
						</button>
					) : (
						<a href="/" className="mb-6 text-sm text-blue-600 hover:text-blue-800 underline">← Home</a>
					)}

					<h1 className="text-3xl flex font-raleway text-gray-800 mb-6 items-center">
						<span className="flex-1">{rental.title || 'Rental Property'}</span>
						<SharePopover url={`https://share.balirentalfinder.com/rental?slug=${rental.slug}`} title={rental.title} data-umami-event="Share button clicked" />
						<span
							className="flex items-center space-x-1 text-sm cursor-pointer hover:bg-gray-100 p-2 rounded-lg"
							onClick={() => toggleLike(rental)}
							data-umami-event="Like button clicked"
						>
							<HeartIcon className={`size-4 text-white ${isLiked ? 'fill-red-500' : 'fill-black/50'}`} />
							<span className="underline">{isLiked ? 'Liked' : 'Like'}</span>
						</span>
					</h1>

					{mediaUrls.length > 0 && (
						<div ref={mediaGridRef} className={`relative gap-2 ${mediaUrls.length > 1 ? 'grid grid-cols-1 md:grid-cols-2' : ''}`}>
							<ImageOrVid mediaUrls={mediaUrls} index={0} isCard={false} onClick={openPhotoGallery} />
							<div className="hidden grid-cols-2 grid-rows-2 gap-2 md:grid">
								{[1, 2, 3, 4].map((i) => (
									<ImageOrVid key={i} mediaUrls={mediaUrls} index={i} isCard={false} onClick={openPhotoGallery} />
								))}
							</div>
							<button
								className="absolute bottom-4 right-4 bg-white text-black border border-gray-900 rounded-md px-3 py-1 text-sm backdrop-blur-sm hover:bg-gray-100"
								onClick={openPhotoGallery}
							>
								View {mediaUrls.length} {mediaUrls.length === 1 ? 'photo' : 'photos'}
							</button>
						</div>
					)}

					{facebookPostUrl && (
						<div className="my-4">
							<FacebookPhotoNotice postUrl={facebookPostUrl} />
						</div>
					)}

					<RentalAttributes rental={rental} expandDetails={false} displayCurrency={displayCurrency} highlightTerms={keywordTerms} />
				</div>
			</div>

			{galleryOpen && (
				<PhotoGalleryOverlay
					mediaUrls={mediaUrls}
					facebookPostUrl={facebookPostUrl}
					onBack={closePhotoGallery}
					onOpenLightbox={openLightbox}
				/>
			)}

			{lightboxIndex !== null && (
				<ImageLightbox
					mediaUrls={mediaUrls}
					facebookPostUrl={facebookPostUrl}
					startIndex={lightboxIndex}
					onClose={closeLightbox}
				/>
			)}
		</div>
	);
};

export default RentalDetail;
